import React, { useRef, useLayoutEffect, useState } from "react";
import type { Position } from ".";
import { InfoTooltip } from "../../../info-tooltip";
import {
  EXTERNAL_LINK_GAP,
  EXTERNAL_LINK_SIZE,
  NAME_GAP,
  NAME_LINE_PADDING,
  NODE_RADIUS,
} from "./constants";

export const GenomesTreeNodeName = ({
  position,
  children,
  isMuted = false,
  isLink = false,
  isDerivative = false,
  hasDownloads = false,
  hasDownloadsWithin = false,
  onClick,
}: {
  position: Position;
  onClick?: () => void;
  isMuted?: boolean;
  isLink?: boolean;
  hasDownloads?: boolean;
  hasDownloadsWithin?: boolean;
  isDerivative?: boolean;
  children?: React.ReactNode;
}) => {
  const [textLength, setTextLength] = useState<number>(0);
  const textRef = useRef<SVGTextElement | null>(null);
  useLayoutEffect(() => {
    if (textRef.current) {
      setTextLength(textRef.current.getComputedTextLength());
    }
  }, []);

  const p = position;
  return (
    <g
      className={
        "genomes-tree__nodes__item" +
        (hasDownloadsWithin ? " genomes-tree__nodes__item--downloads-within" : "") +
        (hasDownloads ? " genomes-tree__nodes__item--download" : "") +
        (isDerivative ? " genomes-tree__nodes__item--derivative" : "") +
        (isMuted ? " genomes-tree__nodes__item--muted" : "") +
        (onClick ? " genomes-tree__nodes__item--clickable" : "")
      }
    >
      {isDerivative ? null : (
        <line x1={p[0]} y1={p[1]} x2={p[0] + NAME_GAP - NAME_LINE_PADDING} y2={p[1]} />
      )}
      {isDerivative ? null : (
        <circle
          onClick={onClick ? () => onClick() : undefined}
          cx={p[0]}
          cy={p[1]}
          r={NODE_RADIUS}
        />
      )}
      <text
        ref={textRef}
        onClick={onClick ? () => onClick() : undefined}
        dominantBaseline="middle"
        x={p[0] + NAME_GAP}
        y={p[1]}
      >
        {children}
      </text>
      {isLink && !isDerivative ? (
        <g
          style={{
            transform: `translate(${
              p[0] + textLength + EXTERNAL_LINK_SIZE + EXTERNAL_LINK_GAP
            }px, ${p[1] - EXTERNAL_LINK_SIZE}px) scale(0.8)`,
          }}
        >
          {/* "Share" button path. Look at `icons.js` */}
          <path d="M10.99 13.99h-9v-9h4.76l2-2H.99c-.55 0-1 .45-1 1v11c0 .55.45 1 1 1h11c.55 0 1-.45 1-1V7.24l-2 2v4.75zm4-14h-5c-.55 0-1 .45-1 1s.45 1 1 1h2.59L7.29 7.28a1 1 0 00-.3.71 1.003 1.003 0 001.71.71l5.29-5.29V6c0 .55.45 1 1 1s1-.45 1-1V1c0-.56-.45-1.01-1-1.01z" />
        </g>
      ) : null}
      {isLink && isDerivative ? (
        <foreignObject
          x={p[0] + textLength + EXTERNAL_LINK_SIZE + EXTERNAL_LINK_GAP}
          y={p[1] - EXTERNAL_LINK_SIZE}
          width={16}
          height={16}
        >
          <InfoTooltip>
            This product is an additional format, from the same organism on this branch. Click the
            product name to link out to its product page for more details.
          </InfoTooltip>
        </foreignObject>
      ) : null}
    </g>
  );
};
